import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNowStrict } from 'date-fns';
import { enUS, lv } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import {
  PhoneCall,
  PhoneForwarded,
  CalendarCheck,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { BOOKING_CHANGE_STYLE } from '@/utils/bookingChange';
import type {
  AppNotification,
  NotificationBookingChange,
} from '@/types/Notification';
import { isActionRequired } from '@/types/Notification';
import { useDateFormat } from '@/hooks/useDateFormat';

interface NotificationItemProps {
  notification: AppNotification;
  onSelect: (notification: AppNotification) => void;
}

const dateFnsLocales: Record<string, Locale> = {
  en: enUS,
  lv_LV: lv,
};

/** Notifications written before `change` existed were all new bookings. */
const DEFAULT_BOOKING_CHANGE: NotificationBookingChange = 'created';

const BOOKING_STATES: Record<
  NotificationBookingChange,
  { icon: typeof CalendarCheck; labelKey: string; tone: string }
> = {
  created: {
    ...BOOKING_CHANGE_STYLE.created,
    labelKey: 'notifications.badges.booked.translation',
  },
  rescheduled: {
    ...BOOKING_CHANGE_STYLE.rescheduled,
    labelKey: 'notifications.badges.moved.translation',
  },
  cancelled: {
    ...BOOKING_CHANGE_STYLE.cancelled,
    labelKey: 'notifications.badges.cancelled.translation',
  },
};

/** Seconds -> "1m 6s" / "48s". */
const formatDuration = (seconds: number | null): string | null => {
  if (seconds === null || seconds <= 0) return null;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return minutes > 0 ? `${minutes}m ${remainder}s` : `${remainder}s`;
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onSelect,
}) => {
  const { t, i18n } = useTranslation();
  const df = useDateFormat();
  const payload = notification.payload ?? {};
  const isUnread = !notification.read_at;
  const needsAction = isActionRequired(notification);

  const relativeTime = formatDistanceToNowStrict(
    new Date(notification.created_at),
    { addSuffix: true, locale: dateFnsLocales[i18n.language] ?? enUS }
  );

  // A callback request is the only thing here that asks the user to do
  // something, so it takes over the title and the icon.
  const title = needsAction
    ? t('notifications.items.callback_requested.translation')
    : t('notifications.items.call_completed.translation');

  const Icon = needsAction ? PhoneForwarded : PhoneCall;

  const caller =
    payload.contact_name?.trim() ||
    payload.caller_id ||
    t('notifications.unknown_caller.translation');

  const duration = formatDuration(payload.duration ?? null);

  const bookingTime = payload.booking
    ? `${df.weekdayShort(payload.booking.start_time)}, ${df.time(payload.booking.start_time)}`
    : null;

  const bookingState = payload.booking
    ? BOOKING_STATES[payload.booking.change ?? DEFAULT_BOOKING_CHANGE]
    : null;
  const BookingIcon = bookingState?.icon;

  const metaLine = [caller, payload.agent_name, duration]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      type="button"
      onClick={() => onSelect(notification)}
      className={cn(
        'flex w-full gap-3 rounded-md px-3 py-3 text-left transition-colors',
        'hover:bg-foreground/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isUnread && 'bg-foreground/[0.04]'
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          needsAction
            ? 'bg-destructive/10 text-destructive'
            : 'bg-brand/10 text-brand'
        )}
      >
        <Icon className="h-4 w-4" />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-baseline justify-between gap-2">
          <span
            className={cn(
              'truncate text-sm',
              isUnread ? 'font-semibold text-foreground' : 'text-foreground/80'
            )}
          >
            {title}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {relativeTime}
          </span>
        </span>

        <span className="truncate text-xs text-muted-foreground">
          {metaLine}
        </span>

        {payload.callback_reason && (
          <span className="line-clamp-2 text-xs text-foreground/70">
            {payload.callback_reason}
          </span>
        )}

        {(payload.is_new_contact || payload.booking) && (
          <span className="flex flex-wrap items-center gap-2 pt-0.5">
            {payload.is_new_contact && (
              <span className="inline-flex items-center gap-1 rounded-md bg-brand/10 px-1.5 py-0.5 text-[11px] font-medium text-brand">
                <Sparkles className="h-3 w-3" />
                {t('notifications.badges.new_customer.translation')}
              </span>
            )}
            {bookingState && BookingIcon && (
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium',
                  bookingState.tone
                )}
              >
                <BookingIcon className="h-3 w-3" />
                {t(bookingState.labelKey, { time: bookingTime })}
              </span>
            )}
          </span>
        )}
      </span>

      {isUnread && (
        <span
          aria-hidden
          className={cn(
            'mt-2 h-2 w-2 shrink-0 self-start rounded-full',
            needsAction ? 'bg-destructive' : 'bg-brand'
          )}
        />
      )}
    </button>
  );
};

export default NotificationItem;
